import React from "react";
import { Button, Callout, Dialog, Field, Textarea } from "../components/DesignSystem";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { useSubmitGuard } from "./common";

/**
 * Product Excellence Pass 1 §10: the generic "I'm interested in unlocking X module" CTA --
 * replaces every locked-module screen previously reusing RequestQuoteAction's stationery-item
 * quote form regardless of which module was actually locked. Creates the same QuoteRequest record
 * (identifying wedding/customer/requested module already, per CommerceService), just tagged with
 * `moduleKey` and without the unrelated stationery item checklist. Stationery/Gifts keep their own
 * specialized RequestQuoteAction unchanged.
 */
export function ModuleInterestAction({ moduleKey, moduleLabel }: { moduleKey: string; moduleLabel: string }) {
  const { service } = usePlannerData();
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const { submitting, guardedSubmit } = useSubmitGuard();

  async function submit() {
    await guardedSubmit(async () => {
      setError(null);
      try {
        await service.commerce.requestQuote({ requestedItems: [moduleLabel], styleNotes: message || undefined, moduleKey });
        setSent(true);
      } catch (err) {
        setError(errorMessage(err));
      }
    });
  }

  return (
    <>
      <Button icon="sparkle" variant="secondary" size="sm" onClick={() => { setOpen(true); setSent(false); }}>Unlock this feature</Button>
      {open && (
        <Dialog
          title={`Interested in ${moduleLabel}?`}
          description="Let us know you'd like this unlocked -- we'll follow up about upgrading your plan."
          width={440}
          onClose={() => setOpen(false)}
          footer={sent ? <Button onClick={() => setOpen(false)}>Done</Button> : (
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting}>{submitting ? "Sending..." : "Send interest"}</Button>
            </>
          )}
        >
          {sent ? (
            <Callout tone="success" title="Thanks -- we've noted your interest">We'll follow up about unlocking {moduleLabel}.</Callout>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              {error && <Callout tone="error" title="Couldn't send that">{error}</Callout>}
              <Field label="Anything you'd like us to know?" hint="Optional">
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={`Why ${moduleLabel} would help your wedding`} />
              </Field>
            </div>
          )}
        </Dialog>
      )}
    </>
  );
}
